<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import { useAuthStore } from '../../stores/authStore';
import { useClipsStore } from '../../stores/clipsStore';

const router = useRouter();
const authStore = useAuthStore();
const clipsStore = useClipsStore();

// Mobile menu state
const isMobileMenuOpen = ref(false);

// Search state
const isSearchExpanded = ref(false);
const searchInputRef = ref<HTMLInputElement | null>(null);
const localQuery = ref(clipsStore.searchQuery);
const language = ref<'tam' | 'eng'>(clipsStore.searchLanguage);

// Debounced search
let searchTimeout: number | null = null;

watch(localQuery, (newQuery) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = window.setTimeout(() => {
    clipsStore.setSearchQuery(newQuery);
    clipsStore.fetchClips();
  }, 300);
});

function setLanguage(lang: 'tam' | 'eng') {
  language.value = lang;
  clipsStore.setSearchLanguage(lang);
}

function toggleSearch() {
  isSearchExpanded.value = !isSearchExpanded.value;
  if (isSearchExpanded.value) {
    setTimeout(() => searchInputRef.value?.focus(), 100);
  }
}

function closeSearch() {
  if (!localQuery.value) {
    isSearchExpanded.value = false;
  }
}

const navItems = [
  { name: 'Dashboard', path: '/', icon: 'grid_view' },
  { name: 'Favorites', path: '/favorites', icon: 'favorite' },
];

// Admin upload link (only shown if isAdmin)
const showAdminUpload = computed(() => authStore.isAdmin);

const currentPath = computed(() => router.currentRoute.value.path);

function handleProfileClick() {
  if (authStore.isAuthenticated) {
    // Could show a dropdown menu
  } else {
    router.push('/login');
  }
}

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false;
}

// Close menu when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.mobile-menu') && !target.closest('.burger-button')) {
    isMobileMenuOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <header class="h-16 border-b border-console-border bg-background-dark px-4 md:px-6 flex items-center justify-between shrink-0 z-50">
    <!-- Logo -->
    <div class="flex items-center gap-3">
      <router-link to="/" class="flex items-center gap-2 md:gap-3">
        <div class="size-9 md:size-10 bg-primary rounded flex items-center justify-center shadow-[0_0_20px_rgba(230,0,119,0.5)]">
          <span class="material-symbols-outlined text-white text-xl md:text-2xl">graphic_eq</span>
        </div>
        <h1 class="text-sm md:text-lg font-bold tracking-tighter uppercase italic hidden sm:block">
          Tamil Soundboard <span class="text-primary italic">D-PRO</span>
        </h1>
      </router-link>
    </div>

    <!-- Search Bar (Desktop only - centered) -->
    <div class="hidden md:flex flex-1 justify-center px-4">
      <!-- Desktop Search -->
      <div class="flex flex-1 max-w-xl items-center bg-console-panel rounded-xl border border-console-border px-4 h-10 shadow-inner group focus-within:border-primary/50 transition-all">
        <span class="material-symbols-outlined text-slate-400 mr-3 group-focus-within:text-primary text-lg">search</span>
        <input
          v-model="localQuery"
          type="text"
          class="bg-transparent border-none focus:ring-0 text-sm w-full placeholder:text-slate-500"
          placeholder="Search memes, actors, dialogues..."
        />
        <div class="flex h-6 bg-black p-0.5 rounded-lg border border-console-border">
          <button
            @click="setLanguage('tam')"
            :class="[
              'px-2 text-[9px] font-bold rounded-md transition-colors',
              language === 'tam' ? 'bg-primary text-white' : 'text-slate-500 hover:text-slate-300'
            ]"
          >
            TAM
          </button>
          <button
            @click="setLanguage('eng')"
            :class="[
              'px-2 text-[9px] font-bold rounded-md transition-colors',
              language === 'eng' ? 'bg-primary text-white' : 'text-slate-500 hover:text-slate-300'
            ]"
          >
            ENG
          </button>
        </div>
      </div>
    </div>

    <!-- Right Side: Nav + Profile + Mobile Search -->
    <div class="flex items-center gap-2 md:gap-6">
      <!-- Mobile Search (Icon + Expandable) - Before burger menu -->
      <div class="md:hidden flex items-center">
        <div
          :class="[
            'flex items-center transition-all duration-300 ease-in-out overflow-hidden',
            isSearchExpanded 
              ? 'fixed inset-x-0 top-0 h-16 bg-background-dark px-4 z-[60] border-b border-console-border' 
              : 'w-10'
          ]"
        >
          <button
            @click="toggleSearch"
            class="flex items-center justify-center size-10 shrink-0"
          >
            <span class="material-symbols-outlined text-slate-400 text-xl hover:text-white">
              {{ isSearchExpanded ? 'close' : 'search' }}
            </span>
          </button>
          <input
            v-if="isSearchExpanded"
            ref="searchInputRef"
            v-model="localQuery"
            type="text"
            class="bg-console-panel border border-console-border rounded-lg px-3 h-10 focus:ring-0 text-sm flex-1 min-w-0 placeholder:text-slate-500 mr-2"
            placeholder="Search memes, actors..."
            @blur="closeSearch"
          />
        </div>
      </div>

      <!-- Desktop Nav -->
      <nav class="hidden lg:flex items-center gap-6 text-xs font-bold uppercase tracking-widest text-slate-400">
        <router-link
          v-for="item in navItems"
          :key="item.name"
          :to="item.path"
          :class="[
            'hover:text-white transition-colors',
            currentPath === item.path ? 'text-primary border-b-2 border-primary pb-1' : ''
          ]"
        >
          {{ item.name }}
        </router-link>
      </nav>

      <!-- Admin Links (only for admins) - Desktop -->
      <div v-if="showAdminUpload" class="hidden md:flex items-center gap-2">
        <router-link
          to="/admin/manage"
          class="flex items-center gap-1 px-2 py-1.5 bg-console-panel border border-console-border rounded-lg text-slate-400 hover:border-primary hover:text-primary transition-colors text-xs font-bold"
        >
          <span class="material-symbols-outlined text-base">settings</span>
          <span class="hidden xl:inline">Manage</span>
        </router-link>
        <router-link
          to="/admin/upload"
          class="flex items-center gap-1 px-2 py-1.5 bg-primary/20 border border-primary rounded-lg text-primary hover:bg-primary hover:text-white transition-colors text-xs font-bold"
        >
          <span class="material-symbols-outlined text-base">upload</span>
          <span class="hidden xl:inline">Upload</span>
        </router-link>
      </div>

      <!-- Profile Section - Desktop -->
      <div class="hidden md:flex items-center gap-3 border-l border-console-border pl-4">
        <button
          @click="handleProfileClick"
          class="size-8 rounded-full border-2 border-primary bg-console-panel flex items-center justify-center overflow-hidden"
        >
          <template v-if="authStore.isAuthenticated">
            <span class="text-primary font-bold text-xs">
              {{ authStore.displayName.charAt(0).toUpperCase() }}
            </span>
          </template>
          <template v-else>
            <span class="material-symbols-outlined text-primary text-lg">person</span>
          </template>
        </button>
      </div>

      <!-- Burger Menu - Mobile -->
      <button
        @click="toggleMobileMenu"
        class="burger-button md:hidden size-10 flex items-center justify-center text-slate-400 hover:text-white"
      >
        <span class="material-symbols-outlined text-2xl">
          {{ isMobileMenuOpen ? 'close' : 'menu' }}
        </span>
      </button>
    </div>

    <!-- Mobile Menu Dropdown -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="isMobileMenuOpen"
          class="mobile-menu fixed top-16 left-0 right-0 bg-console-panel border-b border-console-border shadow-xl z-[100] md:hidden"
        >
          <nav class="p-4 space-y-2">
            <!-- Nav Items -->
            <router-link
              v-for="item in navItems"
              :key="item.name"
              :to="item.path"
              @click="closeMobileMenu"
              :class="[
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                currentPath === item.path 
                  ? 'bg-primary/20 text-primary' 
                  : 'text-slate-300 hover:bg-console-bg'
              ]"
            >
              <span class="material-symbols-outlined">{{ item.icon }}</span>
              <span class="font-medium">{{ item.name }}</span>
            </router-link>

            <!-- Admin Links -->
            <template v-if="showAdminUpload">
              <div class="border-t border-console-border my-2 pt-2">
                <router-link
                  to="/admin/manage"
                  @click="closeMobileMenu"
                  class="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-console-bg transition-colors"
                >
                  <span class="material-symbols-outlined">settings</span>
                  <span class="font-medium">Manage</span>
                </router-link>
                <router-link
                  to="/admin/upload"
                  @click="closeMobileMenu"
                  class="flex items-center gap-3 px-4 py-3 rounded-lg text-primary hover:bg-primary/20 transition-colors"
                >
                  <span class="material-symbols-outlined">upload</span>
                  <span class="font-medium">Upload</span>
                </router-link>
              </div>
            </template>

            <!-- Profile / Login -->
            <div class="border-t border-console-border my-2 pt-2">
              <button
                v-if="authStore.isAuthenticated"
                @click="closeMobileMenu"
                class="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-console-bg transition-colors w-full text-left"
              >
                <div class="size-8 rounded-full border-2 border-primary bg-console-bg flex items-center justify-center">
                  <span class="text-primary font-bold text-xs">
                    {{ authStore.displayName.charAt(0).toUpperCase() }}
                  </span>
                </div>
                <span class="font-medium">{{ authStore.displayName }}</span>
              </button>
              <router-link
                v-else
                to="/login"
                @click="closeMobileMenu"
                class="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-console-bg transition-colors"
              >
                <span class="material-symbols-outlined">login</span>
                <span class="font-medium">Login</span>
              </router-link>
            </div>
          </nav>
        </div>
      </Transition>
    </Teleport>
  </header>
</template>
